import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Calendar,
  Clock,
  Users,
  Edit,
  Trash2,
  Filter,
  Search,
  Save,
  X,
  FileText,
  Eye,
  MapPin,
  Download,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import toast from 'react-hot-toast';
// import Button from '../../../components/common/Button/Button';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import PermissionGate from '../../../components/common/PermissionGate';
import {
  handleTextInputChange,
  handleSearchInputChange,
  handleNumberInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { formatDate } from '../../../utils/stringUtils';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';
import RoutePlanFormModal from '../../../components/crm/RoutePlanFormModal';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV, generateExportFilename } from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';

const RoutePlans = ({ isTodayOnly = false }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get today's date for date validation
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [startDate, setStartDate] = useState(isTodayOnly ? todayStr : '');
  const [endDate, setEndDate] = useState(isTodayOnly ? todayStr : '');
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Sync date filters and clear other filters when isTodayOnly changes
  useEffect(() => {
    // Reset filters
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedAssignee('all');
    setSelectedStatus('all');
    setCurrentPage(1);

    if (isTodayOnly) {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else {
      setStartDate('');
      setEndDate('');
    }
  }, [isTodayOnly, todayStr]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanForEdit, setSelectedPlanForEdit] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const searchInputRef = useRef(null);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [customerSearchTerms, setCustomerSearchTerms] = useState({});

  // Selection and export state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-400 inline" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3 text-brand inline" />
      : <ArrowDown className="ml-1 h-3 w-3 text-brand inline" />;
  };

  // React Query: Delete route plan mutation
  const deleteRoutePlanMutation = useMutation({
    mutationFn: async (routePlanId) => {
      const response = await fieldSalesService.deleteRoutePlan(routePlanId);
      if (response.success) {
        return response.message;
      } else {
        throw new Error(response.error || 'Failed to delete route plan');
      }
    },
    onSuccess: () => {
      toast.success('Route plan deleted successfully!');
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
      // Invalidate and refetch route plans
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete route plan');
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
    }
  });

  // React Query: Bulk delete route plans mutation
  const bulkDeleteRoutePlanMutation = useMutation({
    mutationFn: async (ids) => {
      const response = await fieldSalesService.bulkDeleteRoutePlans(ids);
      if (response.success) {
        return response.message;
      } else {
        throw new Error(response.error || 'Failed to delete route plans');
      }
    },
    onSuccess: (message) => {
      toast.success(message || 'Route plans deleted successfully!');
      setShowBulkDeleteConfirm(false);
      setSelectedRows(new Set());
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete route plans');
      setShowBulkDeleteConfirm(false);
    }
  });

  // React Query: Fetch assignees with search and limit
  const {
    data: assigneesData,
    isLoading: loadingAssignees,
    isError: assigneesError
  } = useQuery({
    queryKey: ['fieldSales', 'salesReps', assigneeSearchTerm],
    queryFn: async () => {
      const params = {
        limit: 10,
        role: 'sales_executive',
        ...(assigneeSearchTerm && assigneeSearchTerm.length >= 2 ? { search: assigneeSearchTerm } : {})
      };
      const response = await fieldSalesService.getSalesReps(params);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load assignees');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to load assignees');
    }
  });

  const assignees = assigneesData || [];

  // React Query: Fetch leads with search and limit (only when modal is open)
  // Note: We'll use a combined search term for all customer dropdowns
  const [leadsSearchTerm, setLeadsSearchTerm] = useState('');

  const {
    data: leadsData,
    isLoading: loadingLeads,
    isError: leadsError
  } = useQuery({
    queryKey: ['fieldSales', 'leadsDropdown', leadsSearchTerm],
    queryFn: async () => {
      const params = {
        limit: 10,
        ...(leadsSearchTerm && leadsSearchTerm.length >= 2 ? { search: leadsSearchTerm } : {})
      };
      const response = await fieldSalesService.getLeadsForDropdown(params);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load leads');
      }
    },
    enabled: showCreateModal, // Only fetch when modal is open
    onError: (error) => {
      toast.error(error.message || 'Failed to load leads');
    }
  });

  const leads = leadsData || [];

  // React Query: Fetch customers for dropdown
  const [customersSearchTerm, setCustomersSearchTerm] = useState('');

  const {
    data: customersData,
    isLoading: loadingCustomers
  } = useQuery({
    queryKey: ['fieldSales', 'customersDropdown', customersSearchTerm],
    queryFn: async () => {
      const params = {
        limit: 10,
        ...(customersSearchTerm && customersSearchTerm.length >= 2 ? { search: customersSearchTerm } : {})
      };
      const response = await fieldSalesService.getCustomersForDropdown(params);
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load customers');
      }
    },
    enabled: showCreateModal,
    onError: (error) => {
      toast.error(error.message || 'Failed to load customers');
    }
  });

  const customers = customersData || [];

  // Convert assignees to SearchableDropdown format
  const assigneeOptions = useMemo(() => {
    let finalAssignees = [...assignees];

    // In edit mode, if the current assignee is not in the list, add it
    if (showCreateModal && selectedPlanForEdit && selectedPlanForEdit.rep) {
      const currentRep = selectedPlanForEdit.rep;
      const repId = typeof currentRep === 'object' ? currentRep._id : currentRep;

      const exists = finalAssignees.some(a => a._id === repId);
      if (!exists && currentRep && typeof currentRep === 'object') {
        // Format if it's an object from the populated route plan
        const roleStr = currentRep.role || (currentRep.user?.role) || 'Unknown';
        const name = currentRep.name ||
          (currentRep.personalInfo ? `${currentRep.personalInfo.firstName} ${currentRep.personalInfo.lastName}` :
            (currentRep.user ? `${currentRep.user.firstName} ${currentRep.user.lastName}` : 'Unknown'));

        finalAssignees.unshift({
          ...currentRep,
          _id: repId,
          name: name,
          role: roleStr,
          department: currentRep.department || 'N/A'
        });
      }
    }

    return finalAssignees.map(assignee => {
      const roleStr = assignee.role ? assignee.role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown';
      return {
        id: assignee._id,
        displayText: `${assignee.name} [${roleStr}]`,
        searchText: `${assignee.name} ${assignee.email} ${assignee.employeeId || ''} ${assignee.role || ''} ${assignee.department || ''}`.toLowerCase(),
        title: assignee.name,
        raw: assignee,
        role: roleStr,
        department: assignee.department || 'N/A',
        renderOption: (opt) => (
          <div className="flex flex-col py-0.5">
            <span className="font-semibold text-gray-900">{opt.title}</span>
            <div className="flex items-center text-[11px] mt-0.5 space-x-2">
              <span className="px-1.5 py-0.5 bg-blue-50 text-[#0F2942] rounded font-medium uppercase tracking-wider">{opt.role}</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-500 italic">{opt.department}</span>
            </div>
          </div>
        )
      };
    });
  }, [assignees, showCreateModal, selectedPlanForEdit]);

  // Convert leads to SearchableDropdown format
  const leadOptions = useMemo(() => {
    return leads.map(lead => ({
      id: lead._id,
      displayText: lead.customerName,
      searchText: `${lead.customerName} ${lead.companyName || ''} ${lead.address || ''} ${lead.leadId || ''} ${lead.email || ''} ${lead.phone || ''} ${lead.alternatePhone || ''}`.toLowerCase(),
      title: lead.customerName,
      leadId: lead.leadId,
      phone: lead.phone,
      email: lead.email,
      company: lead.companyName,
      raw: lead,
      renderOption: (opt) => (
        <div className="flex flex-col py-0.5">
          <span className="font-semibold text-gray-900">{opt.title}</span>
          <div className="flex items-center text-[11px] mt-0.5 space-x-2">
            <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded font-medium tracking-wider">{opt.leadId || 'N/A'}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500">{opt.phone || opt.email || opt.company || 'N/A'}</span>
          </div>
        </div>
      )
    }));
  }, [leads]);

  // Convert customers to SearchableDropdown format
  const customerOptions = useMemo(() => {
    return customers.map(cust => ({
      id: cust._id,
      displayText: cust.customerName,
      searchText: `${cust.customerName} ${cust.companyName || ''} ${cust.address || ''} ${cust.customerId || ''} ${cust.email || ''} ${cust.phone || ''}`.toLowerCase(),
      title: cust.customerName,
      customerId: cust.customerId,
      phone: cust.phone,
      email: cust.email,
      company: cust.companyName,
      raw: cust,
      renderOption: (opt) => (
        <div className="flex flex-col py-0.5">
          <span className="font-semibold text-gray-900">{opt.title}</span>
          <div className="flex items-center text-[11px] mt-0.5 space-x-2">
            <span className="px-1.5 py-0.5 bg-blue-50 text-[#0F2942] rounded font-medium tracking-wider">{opt.customerId || 'N/A'}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500">{opt.phone || opt.email || opt.company || 'N/A'}</span>
          </div>
        </div>
      )
    }));
  }, [customers]);

  // React Query: Fetch route plans with filters and limit
  const {
    data: responseData,
    isLoading: loadingPlans,
    isError: plansError
  } = useQuery({
    queryKey: ['fieldSales', 'routePlans', startDate, endDate, selectedAssignee, selectedStatus, debouncedSearchTerm, currentPage, itemsPerPage],
    queryFn: async () => {
      const filters = {
        page: currentPage,
        limit: itemsPerPage,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(selectedAssignee !== 'all' ? { rep: selectedAssignee } : {}),
        ...(selectedStatus !== 'all' ? { status: selectedStatus } : {}),
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {})
      };
      const response = await fieldSalesService.getRoutePlans(filters);
      if (response.success && response.data) {
        return response;
      } else {
        throw new Error(response.error || 'Failed to load route plans');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to load route plans');
    }
  });

  const plans = responseData?.data || [];
  const pagination = responseData?.pagination || { total: 0, page: 1, limit: 10, pages: 1 };

  const sortedPlans = useMemo(() => {
    let sortableItems = [...plans];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'planStatus') {
          aValue = getPlanStatus(a);
          bValue = getPlanStatus(b);
        } else if (sortConfig.key === 'rep') {
          aValue = (a.rep?.name || (typeof a.rep === 'string' ? a.rep : '')).toString().toLowerCase();
          bValue = (b.rep?.name || (typeof b.rep === 'string' ? b.rep : '')).toString().toLowerCase();
        } else if (sortConfig.key === 'customers') {
          aValue = (a.customers?.[0]?.name || '').toLowerCase();
          bValue = (b.customers?.[0]?.name || '').toLowerCase();
        } else if (sortConfig.key === 'date') {
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
        } else if (sortConfig.key === 'estimatedTime') {
          aValue = Number(a.estimatedTime || 0);
          bValue = Number(b.estimatedTime || 0);
        } else if (sortConfig.key === 'createdAt') {
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [plans, sortConfig]);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, selectedAssignee, selectedStatus, debouncedSearchTerm]);

  const handleCreatePlan = () => {
    setSelectedPlanForEdit(null);
    setEditingPlanId(null);
    setShowCreateModal(true);
  };

  // Handle form save from RoutePlanFormModal
  const handleFormSave = (formData) => {
    // formData.rep now contains the _id of the representative
    const repId = formData.rep;

    if (!repId) {
      toast.error('Please select a valid assignee');
      return;
    }

    // Prepare route plan data - send only IDs
    const routePlanStatus = formData.status || 'scheduled';

    // When editing, update all customer statuses to match the route plan status
    const customerStatus = editingPlanId ? routePlanStatus : (routePlanStatus || 'scheduled');

    const routePlanData = {
      rep: {
        _id: repId
      },
      date: formData.date,
      customers: formData.customers.map((customer) => {
        const customerData = {
          name: customer.name,
          address: customer.address,
          time: customer.time,
          duration: customer.duration || 60,
          status: customerStatus,
          type: customer.type || 'lead'
        };

        // If it's a registered lead or customer, pass the ID
        if (customer._id) {
          if (customer.type === 'customer') {
            customerData.customerId = customer._id;
          } else {
            customerData.leadId = customer._id;
          }
          // The backend will also handle it via customer._id + customer.type
          customerData._id = customer._id;
        }

        return customerData;
      }),
      notes: formData.notes || '',
      status: routePlanStatus
    };

    // Trigger mutation
    if (editingPlanId) {
      updateRoutePlanMutation.mutate({ routePlanId: editingPlanId, routePlanData });
    } else {
      toast.loading('Saving route plan...');
      createRoutePlanMutation.mutate(routePlanData);
    }
  };

  // React Query: Create route plan mutation
  const createRoutePlanMutation = useMutation({
    mutationFn: async (routePlanData) => {
      const response = await fieldSalesService.createRoutePlan(routePlanData);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to save route plan');
      }
    },
    onSuccess: () => {
      toast.success('Route plan saved successfully!');
      setShowCreateModal(false);
      setSelectedPlanForEdit(null);
      setEditingPlanId(null);
      setAssigneeSearchTerm(''); // Reset assignee search
      setLeadsSearchTerm(''); // Reset leads search
      // Invalidate and refetch route plans
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save route plan');
    }
  });

  // React Query: Update route plan mutation
  const updateRoutePlanMutation = useMutation({
    mutationFn: async ({ routePlanId, routePlanData }) => {
      const response = await fieldSalesService.updateRoutePlan(routePlanId, routePlanData);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to update route plan');
      }
    },
    onSuccess: () => {
      toast.success('Route plan updated successfully!');
      setShowCreateModal(false);
      setSelectedPlanForEdit(null);
      setEditingPlanId(null);
      setAssigneeSearchTerm(''); // Reset assignee search
      setLeadsSearchTerm(''); // Reset leads search
      // Invalidate and refetch route plans
      queryClient.invalidateQueries({ queryKey: ['fieldSales', 'routePlans'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update route plan');
    }
  });


  const handleEditPlan = (plan) => {
    setSelectedPlanForEdit(plan);
    setEditingPlanId(plan.id || plan._id || plan.routePlanId);
    setShowCreateModal(true);
  };

  const handleViewMap = (customer) => {
    setSelectedCustomer(customer);
    setShowMapModal(true);
  };

  const handleViewPlan = (plan) => {
    navigate(`/route_plan_details/${plan.id || plan._id || plan.routePlanId}`);
  };

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePlan = () => {
    if (planToDelete) {
      // Use the correct ID property (could be id or _id depending on the data structure)
      const planId = planToDelete.id || planToDelete._id;
      if (planId) {
        deleteRoutePlanMutation.mutate(planId);
      }
    }
  };

  const handleApplyFilter = () => {
    // Filters are automatically applied via useEffect
    toast.success('Filters applied successfully!');
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    const isDateFiltered = isTodayOnly
      ? (startDate !== todayStr || endDate !== todayStr)
      : (startDate !== '' || endDate !== '');

    return debouncedSearchTerm.trim() !== '' ||
      selectedAssignee !== 'all' ||
      selectedStatus !== 'all' ||
      isDateFiltered;
  }, [debouncedSearchTerm, selectedAssignee, selectedStatus, startDate, endDate, isTodayOnly, todayStr]);

  const handleBulkDelete = () => {
    if (selectedRows.size > 0) {
      setShowBulkDeleteConfirm(true);
    }
  };

  const confirmBulkDelete = () => {
    const idsToDelete = Array.from(selectedRows);
    bulkDeleteRoutePlanMutation.mutate(idsToDelete);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedAssignee('all');
    setSelectedStatus('all');
    if (isTodayOnly) {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  function getStatusColor(status) {
    switch (status) {
      case 'completed': return 'text-teal-700 bg-teal-50';
      case 'in-progress':
      case 'in-process': return 'text-[#0F2942] bg-blue-50';
      case 'scheduled': return 'text-gray-600 bg-gray-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  }

  function getPlanStatus(plan) {
    if (!plan.customers || plan.customers.length === 0) return plan.status || 'scheduled';
    let completedCount = 0;
    let cancelledCount = 0;
    let inProgressCount = 0;
    plan.customers.forEach(c => {
      const s = c.status || 'scheduled';
      if (s === 'completed') completedCount++;
      else if (s === 'cancelled') cancelledCount++;
      else if (s === 'in-progress' || s === 'in-process') inProgressCount++;
    });
    const total = plan.customers.length;
    if (completedCount === total) return 'completed';
    if (cancelledCount === total) return 'cancelled';
    if (completedCount + cancelledCount === total) return 'completed';
    if (completedCount > 0 || inProgressCount > 0) return 'in-progress';
    return plan.status || 'scheduled';
  };

  // Format status to human-readable format
  const formatStatus = (status) => {
    if (!status) return 'Scheduled';
    if (status === 'completed') return 'Visited';
    return status
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format time from 24-hour (HH:MM) to 12-hour (HH:MM AM/PM) format
  const formatTimeTo12Hour = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      // Handle time string in format HH:MM or HH:MM:SS
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;

      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];

      if (isNaN(hours) || hours < 0 || hours > 23) return timeString;

      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return newSelection;
    });
  };

  const handleSelectAll = (items) => {
    if (items.length === 0) {
      setSelectedRows(new Set());
      return;
    }

    const allSelected = items.every(item => selectedRows.has(item.id));

    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (allSelected) {
        items.forEach(item => newSelection.delete(item.id));
      } else {
        items.forEach(item => newSelection.add(item.id));
      }
      return newSelection;
    });
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportCurrentPage();
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    try {
      const dataToExport = selectedRows.size > 0
        ? plans.filter(plan => selectedRows.has(plan.id))
        : plans;

      if (dataToExport.length === 0) {
        toast.error('No data to export');
        return;
      }

      const type = selectedRows.size > 0 ? 'Selected Items' : 'Current Page';
      const headers = ['Assigned To', 'Date', 'Estimated Time', 'Customer Name', 'Customer Address', 'Visit Time', 'Duration (minutes)', 'Customer Status', 'Plan Status'];

      const formattedData = [];
      dataToExport.forEach(plan => {
        const rep = plan.rep?.name || (typeof plan.rep === 'string' ? plan.rep : '');
        const date = plan.date ? formatDate(plan.date, { includeTime: false }) : '';
        const estimatedTime = plan.estimatedTime ? `${Math.floor(plan.estimatedTime / 60)}h ${plan.estimatedTime % 60}m` : '';

        if (plan.customers && plan.customers.length > 0) {
          plan.customers.forEach(customer => {
            formattedData.push({
              'Assigned To': rep,
              'Date': date,
              'Estimated Time': estimatedTime,
              'Customer Name': customer.name || '',
              'Customer Address': customer.address || '',
              'Visit Time': formatTimeTo12Hour(customer.time),
              'Duration (minutes)': customer.duration || '',
              'Customer Status': formatStatus(customer.status || 'scheduled')
            });
          });
        } else {
          formattedData.push({
            'Assigned To': rep,
            'Date': date,
            'Estimated Time': estimatedTime,
            'Customer Name': '',
            'Customer Address': '',
            'Visit Time': '',
            'Duration (minutes)': '',
            'Customer Status': ''
          });
        }
      });

      exportToCSV(formattedData, 'route_plans', { title: 'Route Plans', type }, headers);
      toast.success('Route plans exported successfully');
      setSelectedRows(new Set());
    } catch (error) {
      toast.error('Failed to export route plans');
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      if (plans.length === 0) {
        toast.error('No data to export');
        setExportLoading(false);
        return;
      }

      const headers = ['Assigned To', 'Date', 'Estimated Time', 'Customer Name', 'Customer Address', 'Visit Time', 'Duration (minutes)', 'Customer Status'];

      const formattedData = [];
      plans.forEach(plan => {
        const rep = plan.rep?.name || (typeof plan.rep === 'string' ? plan.rep : '');
        const date = plan.date ? formatDate(plan.date, { includeTime: false }) : '';
        const estimatedTime = plan.estimatedTime ? `${Math.floor(plan.estimatedTime / 60)}h ${plan.estimatedTime % 60}m` : '';

        if (plan.customers && plan.customers.length > 0) {
          plan.customers.forEach(customer => {
            formattedData.push({
              'Assigned To': rep,
              'Date': date,
              'Estimated Time': estimatedTime,
              'Customer Name': customer.name || '',
              'Customer Address': customer.address || '',
              'Visit Time': formatTimeTo12Hour(customer.time),
              'Duration (minutes)': customer.duration || '',
              'Customer Status': formatStatus(customer.status || 'scheduled')
            });
          });
        } else {
          formattedData.push({
            'Assigned To': rep,
            'Date': date,
            'Estimated Time': estimatedTime,
            'Customer Name': '',
            'Customer Address': '',
            'Visit Time': '',
            'Duration (minutes)': '',
            'Customer Status': ''
          });
        }
      });

      exportToCSV(formattedData, 'route_plans_all', { title: 'Route Plans', type: 'All Data' }, headers);
      toast.success('All route plans exported successfully');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to export route plans');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const filteredPlans = plans.filter(plan => {
        if (!plan.date) return false;
        const planDate = new Date(plan.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return planDate >= start && planDate <= end;
      });

      if (filteredPlans.length === 0) {
        toast.error('No data found in the selected date range');
        setExportLoading(false);
        return;
      }

      const headers = ['Assigned To', 'Date', 'Estimated Time', 'Customer Name', 'Customer Address', 'Visit Time', 'Duration (minutes)', 'Customer Status'];

      const formattedData = [];
      filteredPlans.forEach(plan => {
        const rep = plan.rep?.name || (typeof plan.rep === 'string' ? plan.rep : '');
        const date = plan.date ? formatDate(plan.date, { includeTime: false }) : '';
        const estimatedTime = plan.estimatedTime ? `${Math.floor(plan.estimatedTime / 60)}h ${plan.estimatedTime % 60}m` : '';

        if (plan.customers && plan.customers.length > 0) {
          plan.customers.forEach(customer => {
            formattedData.push({
              'Assigned To': rep,
              'Date': date,
              'Estimated Time': estimatedTime,
              'Customer Name': customer.name || '',
              'Customer Address': customer.address || '',
              'Visit Time': formatTimeTo12Hour(customer.time),
              'Duration (minutes)': customer.duration || '',
              'Customer Status': formatStatus(customer.status || 'scheduled')
            });
          });
        } else {
          formattedData.push({
            'Assigned To': rep,
            'Date': date,
            'Estimated Time': estimatedTime,
            'Customer Name': '',
            'Customer Address': '',
            'Visit Time': '',
            'Duration (minutes)': '',
            'Customer Status': ''
          });
        }
      });

      const type = `Date Range (${startDate} to ${endDate})`;
      exportToCSV(formattedData, 'route_plans_range', { title: 'Route Plans', type }, headers);
      toast.success('Date range export completed successfully');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to export route plans');
    } finally {
      setExportLoading(false);
    }
  };

  // Filtering is now done on the backend via API

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <MapPin className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">{isTodayOnly ? "Visit Log" : "Route Plans"}</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">{isTodayOnly ? "View and manage today's field sales visits" : "Plan and manage field sales routes"}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedRows.size > 0 && user?.role !== USER_ROLES.SALES_EXECUTIVE && (
            <PermissionGate permission="field_sales" action="delete">
              <button
                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
                onClick={handleBulkDelete}
                disabled={bulkDeleteRoutePlanMutation.isPending}
              >
                {bulkDeleteRoutePlanMutation.isPending ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Trash2 className="h-4 w-4" />}
                <span>Delete ({selectedRows.size})</span>
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="field_sales" action="read">
            <button
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleExportClick}
              disabled={loadingPlans || !plans || plans.length === 0}
            >
              <Download className="h-4 w-4" />
              <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
            </button>
          </PermissionGate>
          <PermissionGate permission="field_sales" action="create">
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleCreatePlan}
            >
              <Plus className="h-4 w-4" />
              <span>Create Plan</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by customer name..."
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
              maxLength={FIELD_LIMITS.search}
              className="input-field pl-10 pr-4 w-full"
              autoComplete="off"
            />
          </div>
          {user?.role !== USER_ROLES.SALES_EXECUTIVE && (
            <div className="w-full sm:w-auto min-w-[200px] max-w-[300px]">
              <SearchableDropdown
                options={assigneeOptions}
                value={selectedAssignee !== 'all' ? selectedAssignee : null}
                onChange={(selectedId) => {
                  setSelectedAssignee(selectedId || 'all');
                }}
                onSearch={(searchTerm) => {
                  setAssigneeSearchTerm(searchTerm);
                }}
                placeholder={loadingAssignees ? 'Loading...' : 'All Assignees'}
                loading={loadingAssignees}
                searchPlaceholder="Search by name or email..."
                minSearchLength={2}
                debounceMs={300}
                className="py-2.5 w-full"
              />
            </div>
          )}
          <div className="w-full sm:w-auto min-w-[150px]">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="input-field w-full"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Visited</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          {!isTodayOnly && (
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate: newStart, endDate: newEnd }) => {
                setStartDate(newStart || '');
                setEndDate(newEnd || '');
              }}
              allowFutureDates={true}
            />
          )}
          {hasActiveFilters && (
            <button
              className="btn-cancel flex items-center justify-center space-x-2 flex-shrink-0 whitespace-nowrap h-9 px-3"
              onClick={handleClearFilters}
            >
              <X className="h-4 w-4" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      {/* Route Plans Table View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                    checked={plans.length > 0 && plans.every(plan => selectedRows.has(plan.id))}
                    onChange={() => handleSelectAll(plans)}
                  />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('customers')}>
                  <div className="flex items-center">
                    Customers
                    {renderSortIcon('customers')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('rep')}>
                  <div className="flex items-center">
                    Assigned To
                    {renderSortIcon('rep')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('date')}>
                  <div className="flex items-center">
                    Visit Date
                    {renderSortIcon('date')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('estimatedTime')}>
                  <div className="flex items-center">
                    Estimated Time
                    {renderSortIcon('estimatedTime')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('planStatus')}>
                  <div className="flex items-center">
                    Status
                    {renderSortIcon('planStatus')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => requestSort('createdAt')}>
                  <div className="flex items-center">
                    Created
                    {renderSortIcon('createdAt')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingPlans ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
                    <p>Loading route plans...</p>
                  </td>
                </tr>
              ) : sortedPlans.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    <p>No route plans found. Create your first route plan!</p>
                  </td>
                </tr>
              ) : (
                sortedPlans.map(plan => (
                  <tr key={plan.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand"
                        checked={selectedRows.has(plan.id)}
                        onChange={() => handleSelectRow(plan.id)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {plan.customers?.length > 0 ? (
                          <>
                            {plan.customers.slice(0, 2).map((customer, idx) => (
                              <div key={idx} className=" text-gray-900 truncate max-w-[150px] capitalize" title={customer.name}>
                                {customer.name}
                              </div>
                            ))}
                            {plan.customers.length > 2 && (
                              <div className="text-xs text-[#0F2942] font-medium cursor-pointer mt-1" onClick={() => handleViewPlan(plan)}>
                                + {plan.customers.length - 2} more customers
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">No customers</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">{plan.rep?.name || (typeof plan.rep === 'string' ? plan.rep : 'Unknown')}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900 font-medium">{formatDate(plan.date, { includeTime: false })}</span>
                        {plan.customers?.[0]?.time && (
                          <span className="text-xs text-gray-500">{formatTimeTo12Hour(plan.customers[0].time)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                        {Math.floor(plan.estimatedTime / 60)}h {plan.estimatedTime % 60}m
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(getPlanStatus(plan))}`}>
                        {formatStatus(getPlanStatus(plan))}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500">
                        {plan.createdAt ? formatDate(plan.createdAt, { includeTime: true }) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewPlan(plan)}
                          className="p-1.5 text-[#0F2942] hover:bg-blue-50 rounded-lg transition-colors"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {user?.role !== USER_ROLES.SALES_EXECUTIVE && (
                          <>
                            <PermissionGate permission="field_sales" action="update">
                              {getPlanStatus(plan) === 'scheduled' && (
                                <button
                                  onClick={() => handleEditPlan(plan)}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                            </PermissionGate>
                            {getPlanStatus(plan) !== 'in-progress' && (
                              <PermissionGate permission="field_sales" action="delete">
                                <button
                                  onClick={() => handleDeletePlan(plan)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {!loadingPlans && pagination.total > itemsPerPage && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <PaginationControls
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              limit={itemsPerPage}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={(page) => setCurrentPage(page)}
              onLimitChange={(limit) => {
                setItemsPerPage(limit);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Plan Modal */}
      <RoutePlanFormModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedPlanForEdit(null);
          setEditingPlanId(null);
          setAssigneeSearchTerm('');
          setLeadsSearchTerm('');
        }}
        mode={editingPlanId ? 'edit' : 'create'}
        initialData={selectedPlanForEdit}
        onSave={handleFormSave}
        repOptions={assigneeOptions}
        loadingReps={loadingAssignees}
        onRepSearch={(searchTerm) => setAssigneeSearchTerm(searchTerm)}
        leadOptions={leadOptions}
        loadingLeads={loadingLeads}
        onLeadSearch={(searchTerm) => setLeadsSearchTerm(searchTerm)}
        customerOptions={customerOptions}
        loadingCustomers={loadingCustomers}
        onCustomerSearch={(searchTerm) => setCustomersSearchTerm(searchTerm)}
        leads={[...leads, ...customers]}
        defaultDate={startDate || todayStr}
        isSaving={createRoutePlanMutation.isPending || updateRoutePlanMutation.isPending}
      />
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && planToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-3 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Confirm Deletion</h3>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the route plan assigned to{' '}
                <span className="font-semibold">
                  {planToDelete.rep?.name || (typeof planToDelete.rep === 'string' ? planToDelete.rep : 'Unknown')} on {formatDate(planToDelete.date, { includeTime: false })}
                </span>
                ?
              </p>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setPlanToDelete(null);
                  }}
                  disabled={deleteRoutePlanMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  className="btn-danger flex items-center justify-center space-x-2"
                  onClick={confirmDeletePlan}
                  disabled={deleteRoutePlanMutation.isPending}
                >
                  {deleteRoutePlanMutation.isPending ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : <Trash2 className="h-4 w-4 mr-2" />}
                  <span>{deleteRoutePlanMutation.isPending ? 'Deleting...' : 'Delete'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-3 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Confirm Bulk Deletion</h3>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete <span className="font-bold">{selectedRows.size}</span> selected route plans? This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="btn-cancel"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteRoutePlanMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  className="btn-danger flex items-center justify-center space-x-2"
                  onClick={confirmBulkDelete}
                  disabled={bulkDeleteRoutePlanMutation.isPending}
                >
                  {bulkDeleteRoutePlanMutation.isPending ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> : <Trash2 className="h-4 w-4 mr-2" />}
                  <span>{bulkDeleteRoutePlanMutation.isPending ? 'Deleting...' : 'Delete Selected'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title="Export Route Plans"
        showDateRange={true}
      />
    </div>
  );
};

export default RoutePlans;
